<template>
  <div class="table-responsive">
    <table :id="id" class="table w-100 table-striped" :class="tableClass" style="table-layout: fixed;">
      <thead :class="headVariant === 'light' ? 'thead-light' : ''">
        <tr>
          <th
            :title="field.label"
            v-for="field in visible_fields"
            :key="field.key"
            @click="toggle_sort(field)"
            :class="field.thClass || null"
            :style="{ cursor: field.sortable ? 'pointer' : 'default' }"
          >
            {{ field.label }}
            <span v-if="field.sortable">
              <i v-if="sort_by === field.key && !sort_desc" class="fa fa-sort-up ml-1"></i>
              <i v-else-if="sort_by === field.key && sort_desc" class="fa fa-sort-down ml-1"></i>
              <i v-else class="fa fa-sort ml-1"></i>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="items.length === 0">
          <td :colspan="visible_fields.length" class="text-center">No data</td>
        </tr>

        <template v-for="(item, index) in sorted_items">
          <!-- Main row: place :key here -->
          <tr
            :key="'row-main-' + getRowKey(item, index)"
            :class="[
              tbodyTrClass ? tbodyTrClass(item) : null,
              { 'table-active': isSelected(item, index) },
              { 'selectable-row': selectable }
            ]"
            @click="row_clicked(item, index)"
          >
            <td
              v-for="field in visible_fields"
              :key="field.key"
              :class="field.tdClass || null"
            >
              <slot :name="`cell(${field.key})`" v-bind="{ item, index, field, value: field.formatter ? field.formatter(null, null, item) : item[field.key] }">
                {{ field.formatter ? field.formatter(null, null, item) : render_item(item, field.key) }}
              </slot>
            </td>
          </tr>

          <!-- Expanded row -->
          <tr
            v-if="isExpanded(item, index)"
            :key="'row-details-' + getRowKey(item, index)"
          >
            <td :colspan="visible_fields.length" class="p-0">
              <slot name="row-details" v-bind="{ item, index }" />
            </td>
          </tr>
        </template>
      </tbody>

    </table>
  </div>
</template>

<script>
export default {
  name: 'MemlistStaticTable',
  emits: ['row-clicked', 'sort-changed'],
  props: {
    id: String,
    fields: Array,
    items: Array,
    headVariant: String,
    tbodyTrClass: Function,
    tableClass: [String, Object, Array],
    selectable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sort_by: null,
      sort_desc: false,
      expandedRows: {},
    }
  },
  computed: {
    visible_fields() {
      return this.fields.filter(f => f.visible !== false)
    },
    sorted_items() {
      if (!this.sort_by) return this.items
      return [...this.items].sort((a, b) => {
        const valA = a[this.sort_by]
        const valB = b[this.sort_by]
        if (valA === valB) return 0
        const res = valA > valB ? 1 : -1
        return this.sort_desc ? -res : res
      })
    },
  },
  methods: {
    isSelected(item, index) {
      return this.selectable && this.getRowKey(item, index) === this.selectedRowKey
    },
    getRowKey(item, index) {
      return item.id ?? `row-${index}`
    },
    render_item(item, key) {
      const keys = key.split('.');

      if (keys.length === 2) {
        return item[keys[0]][keys[1]];
      }

      return item[keys[0]];
    },
    toggle_sort(field) {
      if (!field.sortable) return
      if (this.sort_by === field.key) {
        this.sort_desc = !this.sort_desc
      } else {
        this.sort_by = field.key
        this.sort_desc = false
      }
      this.$emit('sort-changed', {
        sortBy: this.sort_by,
        sortDesc: this.sort_desc,
      })
    },
    row_clicked(record, index) {
      if (!this.selectable) return;
      
      const key = this.getRowKey(record, index)
      this.$set(this.expandedRows, key, !this.expandedRows[key])
      this.$emit('row-clicked', record, index)
    },
    toggleExpanded(item, index) {
      const key = this.getRowKey(item, index)
      this.$set(this.expandedRows, key, !this.expandedRows[key])
    },
    isExpanded(item, index) {
      return !!this.expandedRows[this.getRowKey(item, index)]
    },
  },
}
</script>

<style scoped>
.selectable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.selectable-row:hover {
  background-color: #f5f5f5; /* light gray */
}
</style>